import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { EMPTY, Subject } from 'rxjs';
import { catchError, filter, take, takeUntil, timeout } from 'rxjs/operators';
import { SetOrgTagAction } from 'src/app/auth/store/auth.actions';
import { isAuthenticatedSelector } from 'src/app/auth/store/auth.selectors';
import { Permission } from '@bciti/types';
import { ITokenScope } from 'src/app/auth/auth.model';
import { extractClaimFromJwt } from 'src/app/auth/utils/jwt-claims.util';
import { State } from 'src/app/store';

const AUTH_SETTLE_TIMEOUT_MS = 5_000;

@Component({
    selector: 'app-callback',
    templateUrl: './callback.component.html',
})
export class CallbackComponent implements OnInit, OnDestroy {
    private redirectUrl: string | null = null;
    private destroy$ = new Subject<void>();

    constructor(
        private oidcService: OidcSecurityService,
        private store: Store<State>,
        private router: Router,
        private route: ActivatedRoute,
    ) {}

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit(): void {
        this.redirectUrl = this.route.snapshot.queryParams.redirect ?? null;

        this.oidcService.checkAuth(window.location.href).subscribe({
            next: ({ isAuthenticated, userData, accessToken, configId }) => {
                if (isAuthenticated && configId) {
                    localStorage.setItem('oidc-active-clientId', configId);

                    const org = (userData?.org as string) || extractClaimFromJwt(accessToken, 'org') || undefined;
                    const roles: string[] = extractClaimFromJwt(accessToken, 'roles') || [];
                    const scopes: ITokenScope[] = extractClaimFromJwt(accessToken, 'scopes') || [];
                    const permissions: Permission[] = extractClaimFromJwt(accessToken, 'permissions') || [];
                    if (org) {
                        this.store.dispatch(
                            SetOrgTagAction({
                                org,
                                configId,
                                roles,
                                scopes,
                                permissions,
                            }),
                        );
                        // Wait for the store to process the dispatch before navigating,
                        // otherwise the auth guard's take(1) may read stale state and redirect to /login.
                        this.store
                            .select(isAuthenticatedSelector)
                            .pipe(
                                filter((isAuth) => isAuth),
                                take(1),
                                timeout(AUTH_SETTLE_TIMEOUT_MS),
                                catchError(() => {
                                    this.router.navigate(['/login']);
                                    return EMPTY;
                                }),
                                takeUntil(this.destroy$),
                            )
                            .subscribe(() => {
                                this.router.navigateByUrl(this.redirectUrl ?? '/');
                            });
                    } else {
                        this.router.navigate(['/login']);
                    }
                } else {
                    this.router.navigate(['/login']);
                }
            },
            error: (error) => {
                console.error('[OIDC][Callback] checkAuth error:', error);
                this.router.navigate(['/login']);
            },
        });
    }
}
